/**
 * SpinWheel — the player-facing wheel.
 *
 * Admin's editor and Test dialog draw a SEPARATE copy, `SpinWheelPreview` —
 * web and admin share no component package. The two agree on everything that
 * decides an outcome (equal slices, slice `i` spanning `i * sweep` clockwise
 * from 12 o'clock, labels re-righting from their on-screen angle), which is
 * what makes the preview trustworthy for authoring. They do NOT match
 * pixel-for-pixel: the admin bezel is near-black against this one's grey, and
 * its rim pegs sit on slice boundaries where these sit at slice midpoints. Do
 * not describe the preview as "exactly what a player sees" — an admin comparing
 * the two will find the difference and distrust both. If the packages are ever
 * shared, collapse them into one and the caveat goes away.
 *
 * Slices are EQUAL, one per outcome. Probability decides where the wheel stops,
 * not how large a wedge is: a player must not be able to read the odds off the
 * artwork, and a 50%-weighted outcome drawn as half the wheel would do exactly
 * that. Odds live in the probability meter and the per-outcome fields instead.
 *
 * THE WHEEL SPINS ITSELF. Callers pass `rotation` rather than wrapping this in a
 * rotating element, because only the SLICES may turn: the bezel, the rim pegs
 * and the hub are fixed chrome. Rotating the whole SVG turned the "SPIN" cap
 * upside down along with everything else. Keeping rotation internal also lets
 * each label decide its own orientation from its ON-SCREEN angle, so text stays
 * the right way up wherever the wheel comes to rest.
 */

import React, { ReactElement, useId } from "react";

export interface WheelSegment {
    label: string;
    color?: string;
}

/** Fallback slice colour when an outcome has none authored (or an invalid hex). */
const UNSET_COLOR = "#c7c7ca";
/** A 3- or 6-digit hex. Anything else can't be trusted to a `fill`. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const MAX_LABEL_LINES = 2;

function safeColor(color: string | undefined): string {
    return color && HEX.test(color.trim()) ? color.trim() : UNSET_COLOR;
}

/**
 * Black or white label, whichever survives on this slice. Uses the W3C relative
 * luminance cutoff so a mid-tone red doesn't get unreadable white text.
 */
function labelInk(hex: string): string {
    const h = hex.replace("#", "");
    const full = h.length === 3
        ? h.split("")
            .map(c => c + c)
            .join("")
        : h;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const lin = (c: number) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return luminance > 0.45 ? "#17171b" : "#ffffff";
}

function pointOnCircle(cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } {
    /* -90 so the first slice starts at 12 o'clock, matching the player wheel. */
    const rad = (angleDeg - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function slicePath(cx: number, cy: number, r: number, start: number, end: number): string {
    const from = pointOnCircle(cx, cy, r, start);
    const to = pointOnCircle(cx, cy, r, end);
    const largeArc = end - start > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${from.x} ${from.y} A ${r} ${r} 0 ${largeArc} 1 ${to.x} ${to.y} Z`;
}

/**
 * Greedy word wrap onto at most two lines. SVG has no automatic wrapping, so a
 * long prize name previously had to be cut ("$5 LootShop Cr…"); breaking it at a
 * space keeps the whole name readable. Only the last line is ever ellipsised,
 * and only when a single word genuinely cannot fit.
 */
function wrapLabel(label: string, perLine: number): Array<string> {
    const words = label.trim().split(/\s+/);
    const lines: Array<string> = [];
    let current = "";
    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (candidate.length <= perLine || !current) {
            current = candidate;
        } else {
            lines.push(current);
            current = word;
            if (lines.length === MAX_LABEL_LINES - 1) {
                break;
            }
        }
    }
    const consumed = lines.join(" ")
        .split(/\s+/)
        .filter(Boolean).length;
    const remaining = words.slice(consumed).join(" ");
    lines.push(lines.length === MAX_LABEL_LINES - 1 && remaining ? remaining : current);
    const trimmed = lines.filter(Boolean).slice(0, MAX_LABEL_LINES);
    const last = trimmed[trimmed.length - 1];
    if (last && last.length > perLine) {
        trimmed[trimmed.length - 1] = `${last.slice(0, perLine - 1)}…`;
    }
    return trimmed;
}

export function SpinWheel({ segments, size = 260, hubLabel, rotation = 0, spinDurationMs = 0, variant = "raised" }: {
    segments: Array<WheelSegment>;
    size?: number;
    /** Text for the centre cap (e.g. "SPIN"). Omitted leaves a plain hub. */
    hubLabel?: string;
    /** Degrees the slices are turned. Chrome and hub never move. */
    rotation?: number;
    /** Animate to `rotation` over this long. 0 snaps (used by the static preview). */
    spinDurationMs?: number;
    /**
     * "raised" is the platform look — extruded hub, lit bezel, rim pegs.
     * "flat" is the branded-surface look (rttf-drop-hub.html): thin ring, no
     * pegs, small accent hub reading the surface's own `--o` token, uppercase
     * labels. One wheel, skinned — never forked.
     */
    variant?: "raised" | "flat";
}): ReactElement {
    /*
     * Honour the OS reduced-motion setting: the wheel still travels to the
     * winning segment (the outcome must stay visible) but arrives immediately
     * instead of spinning there for several seconds.
     */
    const prefersReducedMotion = typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const effectiveSpinMs = prefersReducedMotion ? 0 : spinDurationMs;
    /* Unique per instance so two wheels on one page don't share gradient ids. */
    const uid = useId().replace(/:/g, "");
    const cx = size / 2;
    const cy = size / 2;
    const flat = variant === "flat";
    const bezel = flat ? 3 : Math.max(6, size * 0.035);
    const outer = size / 2 - 2;
    const r = outer - bezel;

    if (!segments.length) {
        return (
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Wheel — nothing to show yet">
                <circle cx={cx} cy={cy} r={outer} fill="#f2f2f4" stroke="#ededf0" />
                <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#87878d" fontFamily="IBM Plex Mono, monospace">
                    NO SEGMENTS
                </text>
            </svg>
        );
    }

    const sweep = 360 / segments.length;
    const single = segments.length === 1;
    const description = segments.map(s => s.label).join(", ");
    const hubRadius = hubLabel ? size * 0.131 : size * 0.085;
    /*
     * Extrusion depth. Kept shallow on purpose: a deep offset shows the side wall
     * as a thick crescent under the face, which reads as the cap sliding off its
     * base rather than sitting on it.
     */
    const hubDepth = Math.max(1.5, size * 0.007);
    /*
     * Labels sit at the MIDPOINT between hub and rim, centred in their slice,
     * rather than anchored at the outer edge where they hugged the bezel.
     */
    const labelRadius = (hubRadius + r) / 2;
    const labelBand = r - hubRadius;
    /*
     * Slice labels use the BODY face, not the display face. Big Shoulders is
     * condensed and built for large static headlines; at this size, rotated, its
     * narrow counters close up and 1/I and 0/O collide — exactly the characters
     * prize names are full of ("150XP" reading as "I5OXP"). IBM Plex Sans has
     * open apertures that survive small sizes and rotation. Big Shoulders stays
     * on the hub, where it is large, horizontal and static.
     */
    const fontSize = Math.max(9, size * 0.038);
    const lineHeight = fontSize * 1.15;
    /* Plex is wider than the condensed face, so fewer characters fit per line. */
    const perLine = Math.max(8, Math.round(labelBand / (fontSize * 0.62)));

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={`Wheel, ${segments.length} equal segments: ${description}`}
        >
            <defs>
                {/* Depth: slices darken slightly toward the rim rather than sitting flat. */}
                <radialGradient id={`shade-${uid}`} cx="50%" cy="50%" r="50%">
                    <stop offset="55%" stopColor="#ffffff" stopOpacity="0.10" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
                </radialGradient>
                {/* Bezel gets a light-from-above ramp so the rim reads as a raised ring. */}
                <linearGradient id={`bezel-${uid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6e6e78" />
                    <stop offset="45%" stopColor="#585862" />
                    <stop offset="100%" stopColor="#43434c" />
                </linearGradient>
                {/* Hub: lit from above, so the cap reads as a raised, pressable button. */}
                <linearGradient id={`hub-${uid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="52%" stopColor="#f6f6f9" />
                    <stop offset="88%" stopColor="#e6e6ee" />
                    <stop offset="100%" stopColor="#d3d3dd" />
                </linearGradient>
                <filter id={`drop-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy={size * 0.012} stdDeviation={size * 0.018} floodColor="#000000" floodOpacity="0.28" />
                </filter>
                {/*
                  * Soft edge for the hub's cast shadow only. Deliberately a plain
                  * blur applied to ONE disc rather than a drop-shadow on the hub
                  * group: filtering the group made the face, the stroke and the
                  * bevel each cast their own shadow, and with the blur wider than
                  * the offset it spread evenly in every direction — a grey halo
                  * ringing the cap instead of a shadow falling beneath it.
                  */}
                {/*
                  * Faint shadow behind label text. White on the red slices is the
                  * weakest pairing on the wheel; a tight, low-opacity shadow gives
                  * the letterforms an edge without reading as drop-shadowed text.
                  */}
                <filter id={`labelink-${uid}`} x="-25%" y="-25%" width="150%" height="150%">
                    <feDropShadow dx="0" dy={Math.max(0.5, size * 0.002)} stdDeviation={Math.max(0.4, size * 0.0016)} floodColor="#000000" floodOpacity="0.35" />
                </filter>
                <filter id={`hubshadow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation={size * 0.005} />
                </filter>
                {/* Lit-from-above pass for the accent hub — the same extrusion, brand-colored. */}
                <linearGradient id={`hublit-${uid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
                    <stop offset="55%" stopColor="#ffffff" stopOpacity="0.05" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0.10" />
                </linearGradient>
            </defs>

            {/* Raised outer bezel — fixed chrome, never turns. Flat takes a thin ring. */}
            {flat
                ? <circle cx={cx} cy={cy} r={outer} fill="none" stroke="rgba(255,255,255,0.32)" strokeWidth={2} />
                : <circle cx={cx} cy={cy} r={outer} fill={`url(#bezel-${uid})`} filter={`url(#drop-${uid})`} />}

            {/* Only the slices turn. */}
            <g
                style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: "center",
                    transition: effectiveSpinMs > 0 ? `transform ${effectiveSpinMs}ms cubic-bezier(0.17, 0.67, 0.2, 1)` : "none",
                }}
            >
                {single
                    ? <circle cx={cx} cy={cy} r={r} fill={safeColor(segments[0].color)} />
                    : segments.map((segment, i) =>
                        <path
                            key={`slice-${i}`}
                            d={slicePath(cx, cy, r, i * sweep, (i + 1) * sweep)}
                            fill={safeColor(segment.color)}
                            stroke={flat ? "none" : "#ffffff"}
                            strokeWidth={flat ? 0 : Math.max(1.5, size * 0.006)}
                            strokeLinejoin="round"
                        />)}

                {/* Shading pass over the slices — one overlay, so colours stay authored. */}
                {!flat && <circle cx={cx} cy={cy} r={r} fill={`url(#shade-${uid})`} pointerEvents="none" />}

                {segments.map((segment, i) => {
                    const fill = safeColor(segment.color);
                    const ink = labelInk(fill);
                    if (single) {
                        return (
                            <text key={`label-${i}`} x={cx} y={cy - labelRadius} textAnchor="middle" dominantBaseline="middle" fontSize={fontSize} fontWeight={600} fill={ink} fontFamily="IBM Plex Sans, sans-serif" letterSpacing="0.01em">
                                {segment.label}
                            </text>
                        );
                    }
                    const mid = i * sweep + sweep / 2;
                    /* -90 converts "clock angle" (0 = 12 o'clock) into SVG's 0°-is-east. */
                    const angle = mid - 90;
                    /*
                     * Flip is decided from the label's ON-SCREEN angle — its own
                     * angle plus however far the wheel has turned — so a slice that
                     * rotates past the horizon re-rights itself instead of coming to
                     * rest upside down. Judging it from the static angle alone was
                     * why a spun wheel ended up mirrored.
                     */
                    const onScreen = ((mid + rotation) % 360 + 360) % 360;
                    const flipped = onScreen > 180;
                    const lines = wrapLabel(flat ? segment.label.toUpperCase() : segment.label, perLine);
                    const firstDy = -((lines.length - 1) * lineHeight) / 2;
                    return (
                        <text
                            key={`label-${i}`}
                            transform={`rotate(${angle} ${cx} ${cy}) translate(${cx + labelRadius} ${cy})${flipped ? " rotate(180)" : ""}`}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fontWeight={flat ? 700 : 600}
                            fill={ink}
                            fontFamily="IBM Plex Sans, sans-serif"
                            letterSpacing={flat ? "0.08em" : "0.01em"}
                            filter={`url(#labelink-${uid})`}
                        >
                            {lines.map((line, lineIndex) =>
                                <tspan key={lineIndex} x={0} dy={lineIndex === 0 ? firstDy : lineHeight}>
                                    {line}
                                </tspan>)}
                        </text>
                    );
                })}
            </g>

            {/* Rim pegs — bezel detail, fixed. The flat skin has no bezel to stud. */}
            {!flat && !single && segments.map((_, i) => {
                const peg = pointOnCircle(cx, cy, r + bezel / 2, i * sweep + sweep / 2);
                return (
                    <circle key={`peg-${i}`} cx={peg.x} cy={peg.y} r={Math.max(1.5, size * 0.0115)} fill="#ffffff" opacity={0.9} />
                );
            })}

            {/*
              * Hub — fixed, so "SPIN" never turns with the wheel. Drawn as a
              * raised cap: shadow beneath, lit gradient face, bright top edge.
              */}
            {/*
              * Built as an EXTRUDED button, not a disc with effects stacked on it.
              * Three crisp shapes — contact shadow, the button's side wall, then
              * the face sitting on top of it — read as depth. The previous version
              * layered a blurred drop shadow and a white stroke straddling the same
              * radius, which produced a double ring and a fog halo instead.
              */}
            {/*
              * SAME extruded button both ways — the variant only recolors it.
              * Flat surfaces take the accent token; the platform takes white.
              */}
            <circle cx={cx} cy={cy + hubDepth * 1.8} r={flat ? size * 0.11 : hubRadius} fill="#0b0b0e" opacity={0.18} filter={`url(#hubshadow-${uid})`} />
            {/* Side wall — the darker band you see below a raised cap. */}
            <circle cx={cx} cy={cy + hubDepth} r={flat ? size * 0.11 : hubRadius} fill={flat ? "color-mix(in srgb, var(--o, #F1161D) 58%, #000000)" : "#c4c4ce"} />
            {/* Face. */}
            <circle cx={cx} cy={cy} r={flat ? size * 0.11 : hubRadius} fill={flat ? "var(--o, #F1161D)" : `url(#hub-${uid})`} />
            {flat && <circle cx={cx} cy={cy} r={size * 0.11} fill={`url(#hublit-${uid})`} />}
            {hubLabel &&
                /*
                 * Nudged down by half the extrusion. The cap's visual mass runs
                 * from the face at `cy` to the side wall below it, so its optical
                 * centre sits lower than its geometric one — text placed at exactly
                 * `cy` reads high. `central` also centres more predictably than
                 * `middle` across browsers for all-caps text.
                 */
                <text
                    x={cx}
                    y={flat ? cy : cy + hubDepth / 2}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={flat ? Math.max(10, size * 0.044) : Math.max(11, size * 0.056)}
                    fontWeight={800}
                    fill={flat ? "var(--octa, #ffffff)" : "#17171b"}
                    fontFamily="Big Shoulders Display, sans-serif"
                    letterSpacing="0.06em"
                >
                    {hubLabel}
                </text>}
        </svg>
    );
}

export default SpinWheel;
